import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useTaskAlarm } from './TaskAlarmContext';

const TaskTimerContext = createContext(null);

export const useTaskTimer = () => {
  const ctx = useContext(TaskTimerContext);
  if (!ctx) throw new Error('useTaskTimer must be used within TaskTimerProvider');
  return ctx;
};

// Helpers for localStorage persistence
const LS_TIMERS = 'cv_activeTimers';
const LS_DATA   = 'cv_timerData';

const readLS = (key, fallback) => {
  try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; }
};
const writeLS = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore */ }
};

export const TaskTimerProvider = ({ children }) => {
  const { triggerAlarm } = useTaskAlarm();
  const triggerAlarmRef = useRef(triggerAlarm);
  useEffect(() => { triggerAlarmRef.current = triggerAlarm; }, [triggerAlarm]);

  // Elapsed seconds per task  (taskId -> seconds)  — seeded from localStorage
  const [activeTimers, setActiveTimers] = useState(() => readLS(LS_TIMERS, {}));

  // Which tasks currently have live intervals (source-of-truth for Play/Pause UI)
  const [runningTaskIds, setRunningTaskIds] = useState(new Set());

  const timerIntervals = useRef({}); // taskId -> intervalId
  const elapsedRef      = useRef({}); // taskId -> elapsed seconds (sync, avoids React-batching race)

  // ── persist elapsed times to localStorage every tick ──────────────────────
  useEffect(() => {
    writeLS(LS_TIMERS, activeTimers);
  }, [activeTimers]);

  // ── core interval starter ─────────────────────────────────────────────────
  const _startInterval = useCallback((taskId, task, estimatedSeconds) => {
    if (timerIntervals.current[taskId]) return;

    setRunningTaskIds(prev => new Set([...prev, taskId]));

    timerIntervals.current[taskId] = setInterval(() => {
      // Increment elapsed using a ref — synchronous, no React-batching surprises
      elapsedRef.current[taskId] = (elapsedRef.current[taskId] || 0) + 1;
      const newTime = elapsedRef.current[taskId];

      // Update state purely for UI rendering (no side effects inside updater)
      setActiveTimers(prev => ({
        ...prev,
        [taskId]: estimatedSeconds > 0 ? Math.min(newTime, estimatedSeconds) : newTime,
      }));

      // Completion check — synchronous in setInterval body, not inside state updater
      if (estimatedSeconds > 0 && newTime >= estimatedSeconds) {
        // Stop this interval immediately
        clearInterval(timerIntervals.current[taskId]);
        delete timerIntervals.current[taskId];

        setRunningTaskIds(s => { const ns = new Set(s); ns.delete(taskId); return ns; });

        // Remove from persistent running data
        const timerData = readLS(LS_DATA, {});
        delete timerData[taskId];
        writeLS(LS_DATA, timerData);

        // Get userId from localStorage for the PATCH call
        // Try multiple sources: auth_user.user_id, auth_user.email, or try decoding auth_token
        let userId = null;
        try {
          const authUser = JSON.parse(localStorage.getItem('auth_user') || '{}');
          userId = authUser.user_id || authUser.email || null;
          console.log('[TaskTimerContext] Timer completed! authUser from localStorage:', authUser);
          
          // Fallback: try decoding from auth_token if user_id not found
          if (!userId) {
            const authToken = localStorage.getItem('auth_token');
            if (authToken) {
              try {
                const payload = JSON.parse(atob(authToken.split('.')[1]));
                userId = payload.user_id || payload.email || null;
                console.log('[TaskTimerContext] Fallback: got userId from token:', userId);
              } catch (tokenError) {
                console.error('[TaskTimerContext] Failed to decode auth_token:', tokenError);
              }
            }
          }
        } catch (e) { 
          console.error('[TaskTimerContext] Error parsing auth_user:', e);
        }
        
        if (!userId) {
          console.error('[TaskTimerContext] CRITICAL: No user_id found! Task completion will fail.');
          // Still dispatch events so UI can reflect local state
          window.dispatchEvent(new CustomEvent('taskTimerComplete', { detail: { taskId } }));
          window.dispatchEvent(new CustomEvent('tasksUpdated'));
          triggerAlarmRef.current(task, () => {}, null);
          return;
        }
        
        const userParam = `?user_id=${encodeURIComponent(userId)}`;
        
        // Build the full URL for the PATCH request
        const patchUrl = `${window.location.origin}/api/tasks/${taskId}${userParam}`;
        const patchBody = { status: 'completed', completed: true };
        console.log('[TaskTimerContext] Timer finished for task:', taskId);
        console.log('[TaskTimerContext] Making PATCH request to:', patchUrl);
        console.log('[TaskTimerContext] PATCH body:', JSON.stringify(patchBody));

        // IMMEDIATELY mark task complete in DB + update UI (don't wait for alarm countdown)
        fetch(patchUrl, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(patchBody)
        }).then((response) => {
          console.log('[TaskTimerContext] PATCH response status:', response.status);
          if (!response.ok) {
            console.error('[TaskTimerContext] PATCH failed with status:', response.status);
            return response.text().then(text => {
              console.error('[TaskTimerContext] PATCH error response:', text);
              throw new Error(`PATCH failed: ${response.status} - ${text}`);
            });
          }
          return response.json().then(data => {
            console.log('[TaskTimerContext] PATCH success! Response data:', data);
            // Dispatch tasksUpdated AFTER PATCH resolves so fetchTasks sees completed state
            window.dispatchEvent(new CustomEvent('tasksUpdated'));
          });
        }).catch((error) => {
          console.error('[TaskTimerContext] PATCH request failed:', error);
          // Still dispatch so Tasks.jsx refreshes even on error (optimistic state was set)
          window.dispatchEvent(new CustomEvent('tasksUpdated'));
        });

        // Dispatch event immediately so Tasks.jsx checks the checkbox right away
        window.dispatchEvent(new CustomEvent('taskTimerComplete', { detail: { taskId } }));

        // Fire alarm as notification (task already marked complete above)
        triggerAlarmRef.current(task, () => {}, null);
      }
    }, 1000);
  }, []);

  // ── restore timers from localStorage on app boot ──────────────────────────
  useEffect(() => {
    const timerData = readLS(LS_DATA, {});
    const now = Date.now();

    Object.entries(timerData).forEach(([taskId, data]) => {
      const { task, estimatedSeconds, savedAt, elapsedAtSave } = data;
      if (!task || !savedAt) return;

      // Calculate elapsed since the last save
      const extraSecs  = Math.floor((now - savedAt) / 1000);
      const totalElapsed = (elapsedAtSave || 0) + extraSecs;

      if (estimatedSeconds > 0 && totalElapsed >= estimatedSeconds) {
        // Completed while the page was closed / navigated away — mark complete immediately
        let userId = null;
        try {
          const authUser = JSON.parse(localStorage.getItem('auth_user') || '{}');
          userId = authUser.user_id || authUser.email || null;
          // Fallback: try decoding from auth_token
          if (!userId) {
            const authToken = localStorage.getItem('auth_token');
            if (authToken) {
              try {
                const payload = JSON.parse(atob(authToken.split('.')[1]));
                userId = payload.user_id || payload.email || null;
              } catch { /* ignore */ }
            }
          }
        } catch { /* ignore */ }
        
        if (userId) {
          const userParam = `?user_id=${encodeURIComponent(userId)}`;
          fetch(`${window.location.origin}/api/tasks/${taskId}${userParam}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status: 'completed', completed: true })
          }).then(response => {
            if (!response.ok) {
              console.error('[TaskTimerContext] Restore: PATCH failed for task', taskId);
            }
          }).catch((err) => {
            console.error('[TaskTimerContext] Restore: PATCH error for task', taskId, err);
          });
        } else {
          console.warn('[TaskTimerContext] Restore: No userId found, cannot complete task', taskId);
        }
        window.dispatchEvent(new CustomEvent('taskTimerComplete', { detail: { taskId } }));
        window.dispatchEvent(new CustomEvent('tasksUpdated'));
        // Fire alarm as notification
        triggerAlarmRef.current(task, () => {}, null);

        const updated = readLS(LS_DATA, {});
        delete updated[taskId];
        writeLS(LS_DATA, updated);
      } else {
        // Restore elapsed time and resume the interval
        setActiveTimers(prev => ({ ...prev, [taskId]: totalElapsed }));
        elapsedRef.current[taskId] = totalElapsed; // sync ref so interval resumes from correct offset
        _startInterval(taskId, task, estimatedSeconds);

        // Update the save checkpoint
        const updated = readLS(LS_DATA, {});
        if (updated[taskId]) {
          updated[taskId].elapsedAtSave = totalElapsed;
          updated[taskId].savedAt = now;
          writeLS(LS_DATA, updated);
        }
      }
    });
  // eslint-disable-next-line
  }, []); // Intentionally only on mount — read localStorage snapshot once

  // ── public API ────────────────────────────────────────────────────────────
  const startTimer = useCallback((taskId, task, estimatedSeconds) => {
    // Persist enough info to resume after navigation / tab-close
    const timerData = readLS(LS_DATA, {});
    timerData[taskId] = {
      task,
      estimatedSeconds,
      elapsedAtSave: activeTimers[taskId] || 0,
      savedAt: Date.now(),
    };
    writeLS(LS_DATA, timerData);

    // Sync the elapsed ref so the interval starts from the correct offset
    elapsedRef.current[taskId] = activeTimers[taskId] || 0;

    _startInterval(taskId, task, estimatedSeconds);
  }, [activeTimers, _startInterval]);

  const pauseTimer = useCallback((taskId) => {
    if (timerIntervals.current[taskId]) {
      clearInterval(timerIntervals.current[taskId]);
      delete timerIntervals.current[taskId];
    }
    setRunningTaskIds(prev => { const s = new Set(prev); s.delete(taskId); return s; });

    // Remove from persistent running data (elapsed stays in activeTimers / LS_TIMERS)
    const timerData = readLS(LS_DATA, {});
    delete timerData[taskId];
    writeLS(LS_DATA, timerData);
  }, []);

  const toggleTimer = useCallback((taskId, task, estimatedSeconds) => {
    if (runningTaskIds.has(taskId)) {
      pauseTimer(taskId);
    } else {
      startTimer(taskId, task, estimatedSeconds);
    }
  }, [runningTaskIds, startTimer, pauseTimer]);

  return (
    <TaskTimerContext.Provider value={{ activeTimers, runningTaskIds, startTimer, pauseTimer, toggleTimer }}>
      {children}
    </TaskTimerContext.Provider>
  );
};

export default TaskTimerProvider;
